import React, { useEffect, useState, useCallback } from 'react';
import { getRiskTriggers, createRiskTrigger, updateRiskTrigger, deleteRiskTrigger, getEmployees } from '../../api';

const METRICS = ['Work hours', 'Active hours', 'Idle hours', 'Productive hours'];
const CONDITIONS = ['less than', 'more than', 'equal to'];
const VALUE_OPTIONS = Array.from({ length: 12 }, (_, i) => i + 1);
const LOOKBACK_OPTIONS = [1, 2, 3, 4, 5, 7, 14, 30];

const emptyRule = () => ({ metric: 'Work hours', condition: 'less than', value: '1' });

function ruleText(rules, lookback) {
  if (!rules.length) return '';
  const parts = rules.map(r => `On average, ${r.metric.toLowerCase()} ${r.condition} ${r.value} hour${r.value !== '1' ? 's' : ''}`);
  return parts.join('\nand\n') + `\nIn the last ${lookback} day${lookback > 1 ? 's' : ''}.`;
}

export default function RiskSettings() {
  const [triggers, setTriggers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ title: '', rules: [emptyRule()], lookback_days: 1, excluded_employees: [] });

  const load = useCallback(async () => {
    const [t, e] = await Promise.all([getRiskTriggers(), getEmployees()]);
    setTriggers(t); setEmployees(e);
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditId(null);
    setForm({ title: '', rules: [emptyRule()], lookback_days: 1, excluded_employees: [] });
    setShowModal(true);
  };

  const openEdit = (t) => {
    setEditId(t.id);
    setForm({ title: t.title, rules: t.rules.length ? t.rules : [emptyRule()], lookback_days: t.lookback_days, excluded_employees: t.excluded_employees || [] });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (editId) await updateRiskTrigger(editId, form);
    else await createRiskTrigger(form);
    setShowModal(false);
    load();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this trigger?')) return;
    await deleteRiskTrigger(id);
    load();
  };

  const toggleActive = async (t) => {
    await updateRiskTrigger(t.id, { active: t.active ? 0 : 1 });
    load();
  };

  const updateRule = (i, field, val) => setForm(f => {
    const rules = [...f.rules];
    rules[i] = { ...rules[i], [field]: val };
    return { ...f, rules };
  });

  const addRule = () => setForm(f => ({ ...f, rules: [...f.rules, emptyRule()] }));
  const removeRule = (i) => setForm(f => ({ ...f, rules: f.rules.filter((_, idx) => idx !== i) }));

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Risk User Settings</div>
          <div className="text-muted" style={{ fontSize: '0.875rem', marginTop: 2 }}>Create rules to automatically flag at-risk employees</div>
        </div>
        <button className="btn btn-primary" onClick={openCreate}>Create Risk Trigger</button>
      </div>

      <div className="card">
        <div style={{ color: 'var(--primary)', fontWeight: 700, fontSize: '1.1rem', marginBottom: 16 }}>Risk Triggers</div>
        <div className="table-wrap">
          <table>
            <thead><tr><th>Title</th><th>Rule Condition</th><th></th></tr></thead>
            <tbody>
              {triggers.length === 0 && <tr><td colSpan="3" className="empty">No risk triggers yet. Create one to start monitoring.</td></tr>}
              {triggers.map(t => (
                <tr key={t.id}>
                  <td style={{ fontWeight: 500 }}>{t.title}</td>
                  <td>
                    <div style={{ fontSize: '0.875rem' }}>
                      {t.rules.map((r, i) => (
                        <span key={i}>
                          {i > 0 && <span style={{ color: 'var(--muted)' }}> and </span>}
                          Avg <strong>{r.metric.toLowerCase()} {r.condition} {r.value} hour{r.value !== '1' ? 's' : ''}</strong>
                        </span>
                      ))}
                      {t.rules.length > 0 && <span className="text-muted"> in last {t.lookback_days} day{t.lookback_days > 1 ? 's' : ''}</span>}
                    </div>
                  </td>
                  <td>
                    <div className="flex gap-8">
                      <button className="btn btn-outline btn-sm" onClick={() => openEdit(t)}>✏ Edit</button>
                      <button className="btn btn-outline btn-sm" onClick={() => toggleActive(t)} style={{ color: t.active ? 'var(--warning)' : 'var(--success)' }}>
                        {t.active ? '⏸ Make Inactive' : '▶ Make Active'}
                      </button>
                      <button className="btn btn-danger btn-sm" onClick={() => handleDelete(t.id)}>🗑 Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" style={{ width: 600 }} onClick={e => e.stopPropagation()}>
            <div className="modal-title">Create Risk Trigger</div>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Name of the Trigger <span style={{ color: 'var(--muted)', fontWeight: 400 }}>(Ex: Less working hours)</span></label>
                <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Enter name of the trigger" required />
              </div>

              <div style={{ fontWeight: 600, marginBottom: 12 }}>Risk Trigger Rule</div>
              {form.rules.map((rule, i) => (
                <div key={i}>
                  {i > 0 && <div style={{ textAlign: 'center', color: 'var(--muted)', fontSize: '0.875rem', marginBottom: 8 }}>and</div>}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '0.875rem', color: 'var(--muted)' }}>On average,</span>
                    <select value={rule.metric} onChange={e => updateRule(i, 'metric', e.target.value)} style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border)' }}>
                      {METRICS.map(m => <option key={m} value={m}>{m}</option>)}
                    </select>
                    <span style={{ fontSize: '0.875rem', color: 'var(--muted)' }}>are</span>
                    <select value={rule.condition} onChange={e => updateRule(i, 'condition', e.target.value)} style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border)' }}>
                      {CONDITIONS.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                    <select value={rule.value} onChange={e => updateRule(i, 'value', e.target.value)} style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border)' }}>
                      {VALUE_OPTIONS.map(n => <option key={n} value={String(n)}>{n} hour{n > 1 ? 's' : ''}</option>)}
                    </select>
                    {form.rules.length > 1 && (
                      <button type="button" onClick={() => removeRule(i)} style={{ background: 'none', border: 'none', color: 'var(--danger)', fontSize: '1.1rem', cursor: 'pointer', padding: '0 4px' }}>🗑</button>
                    )}
                    {i === form.rules.length - 1 && (
                      <button type="button" onClick={addRule} style={{ background: 'none', border: 'none', color: 'var(--primary)', fontWeight: 600, cursor: 'pointer', fontSize: '0.875rem' }}>+ Add rule</button>
                    )}
                  </div>
                </div>
              ))}

              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                <span style={{ fontSize: '0.875rem', color: 'var(--muted)' }}>In the last</span>
                <select value={form.lookback_days} onChange={e => setForm(f => ({ ...f, lookback_days: parseInt(e.target.value) }))} style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border)' }}>
                  {LOOKBACK_OPTIONS.map(n => <option key={n} value={n}>{n} day{n > 1 ? 's' : ''}</option>)}
                </select>
              </div>

              <div className="form-group">
                <label>Exclude User from the Trigger (optional)</label>
                <select multiple value={form.excluded_employees} onChange={e => setForm(f => ({ ...f, excluded_employees: Array.from(e.target.selectedOptions, o => o.value) }))} style={{ height: 80, padding: '4px 8px', border: '1px solid var(--border)', borderRadius: 8 }}>
                  {employees.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
                </select>
                <div className="text-muted" style={{ fontSize: '0.75rem', marginTop: 2 }}>Hold Ctrl/Cmd to select multiple</div>
              </div>

              {/* Live preview */}
              <div style={{ background: '#f8fafc', border: '1px solid var(--border)', borderRadius: 8, padding: '12px 16px', marginBottom: 16, fontSize: '0.875rem', color: 'var(--muted)', textAlign: 'center', whiteSpace: 'pre-line' }}>
                {ruleText(form.rules, form.lookback_days)}
              </div>

              <div className="flex gap-8" style={{ justifyContent: 'flex-end' }}>
                <button type="button" className="btn btn-outline" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" style={{ flex: 1, justifyContent: 'center' }}>Create Risk Trigger</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
